import { useGlobalMediaPlayer } from "@/hooks/useGlobalMediaPlayer";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { IVideoPlayerProps, IVideoPlayerRef } from "../index";
const MIN_PLAYBACK_RATE = 0.0625; // 1/16, common browser floor
const MAX_PLAYBACK_RATE = 16.0;

export const useVideoPlayerLogic = (
  props: IVideoPlayerProps,
  ref: React.ForwardedRef<IVideoPlayerRef>,
) => {
  const {
    src,
    enableHoverPlay = false,
    disableClickPlay = false,
    onTimeUpdate,
    range,
    stopOnUnmount = true,
  } = props;

  const [isVideoEnabled, setIsVideoEnabled] = useState(
    !enableHoverPlay && !disableClickPlay,
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const hoverTimerRef = useRef<NodeJS.Timeout>(undefined);
  const playPromiseRef = useRef<Promise<void>>(undefined);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { isPlaying, handlePlay, handlePause, pause } =
    useGlobalMediaPlayer(videoRef);

  useImperativeHandle(ref, () => ({
    play: () => {
      if (videoRef.current && !playPromiseRef.current) {
        playPromiseRef.current = videoRef.current
          .play()
          .catch((e) => {
            console.error("Video play error:", e);
          })
          .finally(() => {
            playPromiseRef.current = undefined;
          });
      }
    },
    pause: () => {
      videoRef.current?.pause();
      playPromiseRef.current = undefined;
    },
    getVideoElement: () => videoRef.current,
    getDuration: () => videoRef.current?.duration || 0,
  }));

  const clipMetadata = useMemo(() => {
    if (!props.enableClipRange || !props.clipRange || duration <= 0) {
      return null;
    }

    const { startTime, duration: clipDuration } = props.clipRange;
    const availableInVideo = duration - startTime;
    const rawRate =
      clipDuration > 0 && availableInVideo < clipDuration
        ? availableInVideo / clipDuration
        : 1;
    const playbackRate = Math.min(
      MAX_PLAYBACK_RATE,
      Math.max(MIN_PLAYBACK_RATE, rawRate),
    );
    const endTime = startTime + clipDuration * playbackRate;

    return { startTime, endTime, playbackRate };
  }, [
    props.enableClipRange,
    props.clipRange?.startTime,
    props.clipRange?.duration,
    duration,
  ]);

  const onTimeUpdateRef = useRef(onTimeUpdate);
  onTimeUpdateRef.current = onTimeUpdate;

  useEffect(() => {
    if (!isVideoEnabled || !videoRef.current) {
      return;
    }

    const currentVideo = videoRef.current;

    currentVideo.addEventListener("play", handlePlay);
    currentVideo.addEventListener("pause", handlePause);

    const onTimeUpdateFunc = (event: Event) => {
      const target = event.currentTarget as HTMLVideoElement;
      if (!target) return;

      const currentTime = target.currentTime;
      // PERFORMANCE OPTIMIZATION:
      // We removed setCurrentTime(currentTime) here to prevent the entire component tree
      // from re-rendering 60 times per second.
      // Components that need to track time (like progress bars) should listen to
      // the timeupdate event directly on the video element via Context.videoRef.

      // Handle onTimeUpdate callback
      onTimeUpdateRef.current?.(currentTime);

      // Handle fixed range restriction (original logic)
      if (
        range?.start !== undefined &&
        range?.end !== undefined &&
        range.start <= range.end &&
        (currentTime < range.start || currentTime > range.end)
      ) {
        target.currentTime = range.start;
        return;
      }

      // Handle clip range restriction (new logic)
      if (clipMetadata) {
        const { startTime, endTime } = clipMetadata;

        if (currentTime < startTime || currentTime >= endTime) {
          if (props.loop) {
            target.currentTime = startTime;
          } else {
            target.pause();
            target.currentTime = startTime;
          }
        }
      }
    };

    const onError = () => {
      setError("Video failed to load");
    };
    currentVideo.addEventListener("error", onError);

    const onLoadedMetadata = () => {
      setDuration(currentVideo.duration);
      if (clipMetadata) {
        const { startTime, playbackRate } = clipMetadata;
        currentVideo.playbackRate = playbackRate;
        currentVideo.currentTime = startTime;
      }
    };
    currentVideo.addEventListener("loadedmetadata", onLoadedMetadata);

    currentVideo.addEventListener("timeupdate", onTimeUpdateFunc);

    return () => {
      currentVideo.removeEventListener("play", handlePlay);
      currentVideo.removeEventListener("pause", handlePause);
      currentVideo.removeEventListener("timeupdate", onTimeUpdateFunc);
      currentVideo.removeEventListener("error", onError);
      currentVideo.removeEventListener("loadedmetadata", onLoadedMetadata);
      if (stopOnUnmount) {
        handlePause();
      }
    };
  }, [
    range?.start,
    range?.end,
    props.enableClipRange,
    props.clipRange?.startTime,
    props.clipRange?.duration,
    isVideoEnabled,
    handlePlay,
    handlePause,
    stopOnUnmount,
    clipMetadata,
  ]);

  useEffect(() => {
    if (!videoRef.current || !src) {
      return;
    }

    videoRef.current.load();
    setIsLoaded(false);
  }, [src]);

  useEffect(() => {
    if (clipMetadata && videoRef.current && isLoaded) {
      const { startTime, playbackRate } = clipMetadata;
      videoRef.current.playbackRate = playbackRate;

      // Only sync if the gap is significant and we aren't currently seeking/dragging
      if (Math.abs(videoRef.current.currentTime - startTime) > 0.5) {
        videoRef.current.currentTime = startTime;
      }
    }
  }, [clipMetadata, isLoaded]);

  useEffect(() => {
    const currentVideo = videoRef.current;
    return () => {
      if (!stopOnUnmount) return;

      // Clear any pending play promise - don't wait for it
      playPromiseRef.current = undefined;

      handlePause();

      if (typeof window !== "undefined" && hoverTimerRef.current) {
        window.clearTimeout(hoverTimerRef.current);
      }

      if (currentVideo) {
        try {
          currentVideo.pause();
          currentVideo.currentTime = 0;
        } catch (error) {
          console.error("Video cleanup error:", error);
        }
      }
    };
  }, [stopOnUnmount, handlePause]);

  // Always cleanup hoverTimerRef on unmount, regardless of stopOnUnmount setting
  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = undefined;
      }
    };
  }, []);

  const handleStartPlay = useCallback(() => {
    if (!isPlaying && !playPromiseRef.current) {
      setIsVideoEnabled(true);
      // Clear any existing timer before setting a new one
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = undefined;
      }
      hoverTimerRef.current = setTimeout(() => {
        if (videoRef.current?.src) {
          playPromiseRef.current = videoRef.current
            .play()
            .catch((e) => {
              console.error("Video play error:", e);
            })
            .finally(() => {
              playPromiseRef.current = undefined;
            });
        }
      }, 500);
    }
  }, [isPlaying]);

  const handleStopPlay = useCallback(async () => {
    setIsVideoEnabled(false);
    handlePause();
    if (window && hoverTimerRef.current) {
      window?.clearTimeout(hoverTimerRef.current);
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    if (playPromiseRef.current) {
      await playPromiseRef.current;
      playPromiseRef.current = undefined;
    }
  }, [handlePause]);

  const handleMouseEnter = useCallback(() => {
    if (enableHoverPlay) {
      handleStartPlay();
    }
  }, [enableHoverPlay, handleStartPlay]);

  const handleMouseLeave = useCallback(() => {
    if (enableHoverPlay) {
      handleStopPlay();
    }
  }, [enableHoverPlay, handleStopPlay]);

  const handleExpand = useCallback(() => {
    videoRef.current?.requestFullscreen();
  }, []);

  const seekTo = useCallback((time: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = time;
  }, []);

  return {
    videoRef,
    isLoaded,
    setIsLoaded,
    handleMouseEnter,
    handleMouseLeave,
    handleExpand,
    pause,
    seekTo,
    isPlaying,
    duration,
    error,
  };
};
